import { useEffect, useState } from "react";
import { Alert, Button, Col, Form, Modal, Row, Spinner } from "react-bootstrap";
import { FaTrash } from "react-icons/fa";
import APICall from "../../APICalls/APICall";

// Built-in default text for each bot/escalation message. MUST stay in sync with
// chat_service ChatController._BOT_MSG_DEFAULTS. Fields are pre-filled with these
// so an admin can read and understand each message; a field left equal to its
// default is saved as blank (null) so the backend keeps using the live default.
const MESSAGE_DEFAULTS = {
  welcomeMessage:         "Hello! Welcome to Chatbot.\nPick a topic below, or just type your question and I'll help:",
  fallbackMessage:        "Sorry, I didn't quite get that. Please try rephrasing your question, or pick an option below.",
  escConnectingMessage:   "Connecting you to a human agent. Please wait a moment.",
  escEscalatedMessage:    "Your request has been escalated to a human agent.",
  escDomainMessage:       "I'm connecting you to a human agent who can help with this.",
  escNomatchMessage:      "I'm still not sure I understand — let me connect you to a human agent who can help.",
  reasonLowConfidence:    "Low confidence or handoff keyword detected",
  reasonQuickReply:       "User selected escalation quick reply",
  reasonDomainNotMatched: "Auto-escalation: domain not matched",
  reasonNoMatch:          "No-match fallback exhausted",
};

const SettingsConversation = () => {
  const DEFAULTS = {
    confidenceThreshold: 60,
    contextTimeout: 30,
    maxConversationLength: 50,
    enableSentiment: false,
    description: "",
    enableAutoEscalation: false,
    escalationAttempts: 3,
    escalateOnNegative: false,
    escalationKeywords: [],
    agentReplyTimeout: "",   // seconds; "" → use backend default
    agentWaitMessage: "",    // "" → use backend default text
    agentReassignEnabled: true,
    agentReassignTimeout: "",     // seconds; "" → use backend default
    agentReassignMessage: "",     // "" → use backend default text
    agentUnavailableMessage: "",  // "" → use backend default text
    // Bot & escalation message templates — pre-filled with the built-in defaults
    // so they're visible; an unchanged value is saved as null (= keep default).
    ...MESSAGE_DEFAULTS,
    // CSAT rating prompt (admin-controlled)
    csatEnabled: true,
    csatQuestion: "How was your chat?",
    csatTrigger: "on_close",
  };

  const [settings,            setSettings]            = useState(DEFAULTS);
  const [savedSettings,       setSavedSettings]       = useState(null);
  const [hasChanges,          setHasChanges]          = useState(false);
  const [lastSavedAt,         setLastSavedAt]         = useState(null);
  const [loading,             setLoading]             = useState(true);
  const [saving,              setSaving]              = useState(false);
  const [error,               setError]               = useState("");
  const [success,             setSuccess]             = useState("");
  const [contextTimeoutError, setContextTimeoutError] = useState("");
  const [maxConvError,        setMaxConvError]        = useState("");
  const [showKeywordModal,    setShowKeywordModal]    = useState(false);
  const [newKeyword,          setNewKeyword]          = useState("");
  const [newPriority,         setNewPriority]         = useState("HIGH");

  /* ── Load from API ── */
  useEffect(() => {
    const load = async () => {
      try {
        const res = await APICall.getT("/settings/conversation");
        const ai  = res.ai_configuration  || {};
        const esc = res.auto_escalation   || {};
        const kws = res.escalation_keywords || [];
        const bm  = res.bot_messages || {};
        const cs  = res.csat || {};

        const loaded = {
          confidenceThreshold:   ai.confidence_threshold    ?? 60,
          contextTimeout:        ai.context_timeout         ?? 30,
          maxConversationLength: ai.max_conversation_length ?? 50,
          enableSentiment:       ai.sentiment_analysis      ?? false,
          description:           ai.description             ?? "",
          enableAutoEscalation:  esc.auto_escalation_enabled ?? false,
          escalationAttempts:    esc.escalate_after_failures  ?? 3,
          escalateOnNegative:    esc.escalate_on_negative     ?? false,
          escalationKeywords:    kws.map(k => ({ label: k.keyword, priority: k.priority })),
          agentReplyTimeout:     esc.agent_reply_timeout_seconds ?? "",
          agentWaitMessage:      esc.agent_wait_message          ?? "",
          agentReassignEnabled:  esc.agent_reassign_enabled ?? true,
          agentReassignTimeout:  esc.agent_reassign_timeout_seconds ?? "",
          agentReassignMessage:  esc.agent_reassign_message         ?? "",
          agentUnavailableMessage: esc.agent_unavailable_message    ?? "",
          // Pre-fill with the built-in default when the org hasn't set a custom
          // value, so the admin can see the actual message text.
          welcomeMessage:         bm.welcome_message               ?? MESSAGE_DEFAULTS.welcomeMessage,
          fallbackMessage:        bm.fallback_message              ?? MESSAGE_DEFAULTS.fallbackMessage,
          escConnectingMessage:   bm.escalation_connecting_message ?? MESSAGE_DEFAULTS.escConnectingMessage,
          escEscalatedMessage:    bm.escalation_escalated_message  ?? MESSAGE_DEFAULTS.escEscalatedMessage,
          escDomainMessage:       bm.escalation_domain_message     ?? MESSAGE_DEFAULTS.escDomainMessage,
          escNomatchMessage:      bm.escalation_nomatch_message    ?? MESSAGE_DEFAULTS.escNomatchMessage,
          reasonLowConfidence:    bm.reason_low_confidence         ?? MESSAGE_DEFAULTS.reasonLowConfidence,
          reasonQuickReply:       bm.reason_quick_reply            ?? MESSAGE_DEFAULTS.reasonQuickReply,
          reasonDomainNotMatched: bm.reason_domain_not_matched     ?? MESSAGE_DEFAULTS.reasonDomainNotMatched,
          reasonNoMatch:          bm.reason_no_match               ?? MESSAGE_DEFAULTS.reasonNoMatch,
          csatEnabled:            cs.enabled  ?? true,
          csatQuestion:           cs.question ?? "How was your chat?",
          csatTrigger:            cs.trigger  ?? "on_close",
        };
        setSettings(loaded);
        setSavedSettings(loaded);
      } catch (err) {
        setError("Failed to load settings. " + (err.message || ""));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  /* ── Track unsaved changes ── */
  useEffect(() => {
    if (!savedSettings) return;
    const { escalationKeywords: _a, ...rest }  = settings;
    const { escalationKeywords: _b, ...saved } = savedSettings;
    setHasChanges(JSON.stringify(rest) !== JSON.stringify(saved));
  }, [settings, savedSettings]);

  /* ── Save to API ── */
  const handleSave = async () => {
    if (contextTimeoutError || maxConvError) return;
    // A message field left blank or unchanged from its default → null (keep the
    // backend default); a genuine edit → the trimmed custom text.
    const msgOrNull = (key) => {
      const v = (settings[key] || "").trim();
      return (!v || v === (MESSAGE_DEFAULTS[key] || "").trim()) ? null : v;
    };
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      await APICall.postT("/settings/conversation", {
        ai_configuration: {
          description:             settings.description,
          confidence_threshold:    settings.confidenceThreshold,
          context_timeout:         settings.contextTimeout,
          max_conversation_length: settings.maxConversationLength,
          sentiment_analysis:      settings.enableSentiment,
        },
        auto_escalation: {
          auto_escalation_enabled: settings.enableAutoEscalation,
          escalate_after_failures: settings.escalationAttempts,
          escalate_on_negative:    settings.escalateOnNegative,
          agent_reply_timeout_seconds:
            settings.agentReplyTimeout === "" ? null : Number(settings.agentReplyTimeout),
          agent_wait_message: settings.agentWaitMessage.trim() || null,
          agent_reassign_enabled: settings.agentReassignEnabled,
          agent_reassign_timeout_seconds:
            settings.agentReassignTimeout === "" ? null : Number(settings.agentReassignTimeout),
          agent_reassign_message: settings.agentReassignMessage.trim() || null,
          agent_unavailable_message: settings.agentUnavailableMessage.trim() || null,
        },
        bot_messages: {
          // Send null when the field is blank OR still equal to the default, so
          // unchanged messages keep tracking the backend's live default.
          welcome_message:               msgOrNull("welcomeMessage"),
          fallback_message:              msgOrNull("fallbackMessage"),
          escalation_connecting_message: msgOrNull("escConnectingMessage"),
          escalation_escalated_message:  msgOrNull("escEscalatedMessage"),
          escalation_domain_message:     msgOrNull("escDomainMessage"),
          escalation_nomatch_message:    msgOrNull("escNomatchMessage"),
          reason_low_confidence:         msgOrNull("reasonLowConfidence"),
          reason_quick_reply:            msgOrNull("reasonQuickReply"),
          reason_domain_not_matched:     msgOrNull("reasonDomainNotMatched"),
          reason_no_match:               msgOrNull("reasonNoMatch"),
        },
        csat: {
          enabled:  settings.csatEnabled,
          question: settings.csatQuestion.trim() || "How was your chat?",
          trigger:  settings.csatTrigger,
        },
      });
      setSavedSettings({ ...settings });
      setLastSavedAt(new Date());
      setHasChanges(false);
      setSuccess("Conversation settings saved.");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.message || "Failed to save settings.");
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    if (!savedSettings) return;
    setSettings({ ...savedSettings });
    setHasChanges(false);
    setContextTimeoutError("");
    setMaxConvError("");
  };

  const addKeyword = () => {
    if (!newKeyword.trim()) return;
    setSettings(s => ({
      ...s,
      escalationKeywords: [...s.escalationKeywords, { label: newKeyword.trim(), priority: newPriority }],
    }));
    setNewKeyword("");
    setNewPriority("HIGH");
    setShowKeywordModal(false);
  };

  const updatePriority = (index, priority) => {
    setSettings(s => {
      const kws = [...s.escalationKeywords];
      kws[index] = { ...kws[index], priority };
      return { ...s, escalationKeywords: kws };
    });
  };

  const removeKeyword = (index) =>
    setSettings(s => ({ ...s, escalationKeywords: s.escalationKeywords.filter((_, i) => i !== index) }));

  const set = (patch) => setSettings(s => ({ ...s, ...patch }));

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <>
      {error   && <Alert variant="danger"  dismissible onClose={() => setError("")}  className="mb-3">{error}</Alert>}
      {success && <Alert variant="success" dismissible onClose={() => setSuccess("")} className="mb-3">{success}</Alert>}

      {/* AI CONFIGURATION */}
      <h6 className="text-primary mb-3">AI Configuration</h6>

      <Row className="mb-3">
        <Col md={6} className="mb-3">
          <Form.Label className="fw-semibold">
            Confidence Threshold
            <span className="text-primary ms-2">({settings.confidenceThreshold}%)</span>
          </Form.Label>
          <Form.Range
            min={0} max={100}
            value={settings.confidenceThreshold}
            onChange={(e) => set({ confidenceThreshold: Number(e.target.value) })}
          />
          <Form.Label className="mt-2">Description</Form.Label>
          <Form.Control
            as="textarea" rows={3}
            value={settings.description}
            onChange={(e) => set({ description: e.target.value })}
            placeholder="Optional description for this AI configuration"
          />
        </Col>

        <Col md={6} className="mb-3">
          <Form.Label>Context Timeout (minutes)</Form.Label>
          <Form.Control
            type="number"
            value={settings.contextTimeout === 0 ? "" : settings.contextTimeout}
            isInvalid={!!contextTimeoutError}
            onChange={(e) => {
              const v = e.target.value === "" ? "" : Number(e.target.value);
              set({ contextTimeout: v });
              if (v !== "" && v < 5)        setContextTimeoutError("Must be at least 5 minutes");
              else if (v !== "" && v > 120) setContextTimeoutError("Cannot exceed 120 minutes");
              else                          setContextTimeoutError("");
            }}
          />
          <Form.Control.Feedback type="invalid">{contextTimeoutError}</Form.Control.Feedback>
        </Col>

        <Col md={6} className="mb-3">
          <Form.Label>Max Conversation Length (messages)</Form.Label>
          <Form.Control
            type="number"
            value={settings.maxConversationLength}
            isInvalid={!!maxConvError}
            onChange={(e) => {
              const v = e.target.value;
              set({ maxConversationLength: v });
              const n = Number(v);
              if (v === "")     setMaxConvError("");
              else if (n < 10)  setMaxConvError("Must be at least 10 messages");
              else if (n > 500) setMaxConvError("Cannot exceed 500 messages");
              else              setMaxConvError("");
            }}
          />
          <Form.Control.Feedback type="invalid">{maxConvError}</Form.Control.Feedback>
        </Col>

        <Col md={6} className="d-flex align-items-center">
          <Form.Check
            type="switch"
            label="Enable Sentiment Analysis"
            checked={settings.enableSentiment}
            onChange={(e) => set({ enableSentiment: e.target.checked })}
          />
        </Col>
      </Row>

      <hr />

      {/* AUTO ESCALATION */}
      <h6 className="text-primary mb-3">Auto-Escalation Rules</h6>

      <Form.Check
        type="switch"
        label="Enable Auto-Escalation"
        checked={settings.enableAutoEscalation}
        onChange={(e) => set({ enableAutoEscalation: e.target.checked })}
        className="mb-3"
      />

      {settings.enableAutoEscalation && (
        <>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Label>Escalate after X failed attempts</Form.Label>
              <Form.Control
                type="number" min={1}
                value={settings.escalationAttempts}
                onChange={(e) => set({ escalationAttempts: Number(e.target.value) })}
              />
            </Col>
          </Row>

          <Row className="mb-4">
            <Col>
              <Form.Check
                type="checkbox"
                label="Escalate on negative sentiment"
                checked={settings.escalateOnNegative}
                onChange={(e) => set({ escalateOnNegative: e.target.checked })}
              />
            </Col>
          </Row>

          <hr />

          <Row className="align-items-center mb-2">
            <Col xs={8} md={6}>
              <h6 className="text-primary mb-0">Escalation Keywords</h6>
            </Col>
            <Col xs={4} md={6} className="text-end">
              <Button size="sm" variant="primary" onClick={() => setShowKeywordModal(true)}>
                Add Keyword
              </Button>
            </Col>
          </Row>

          {settings.escalationKeywords.length === 0 && (
            <p className="text-muted small">No keywords added yet.</p>
          )}

          {settings.escalationKeywords.map((item, index) => (
            <Row key={`${item.label}-${index}`} className="align-items-center mb-1">
              <Col xs="auto" className="pe-2">{item.label}</Col>
              <Col>
                <div className="d-flex align-items-center gap-2">
                  <Form.Select
                    size="sm"
                    value={item.priority}
                    onChange={(e) => updatePriority(index, e.target.value)}
                  >
                    <option value="HIGH">High</option>
                    <option value="MEDIUM">Medium</option>
                    <option value="LOW">Low</option>
                  </Form.Select>
                  <Button size="sm" variant="outline-danger" onClick={() => removeKeyword(index)}>
                    <FaTrash />
                  </Button>
                </div>
              </Col>
            </Row>
          ))}
        </>
      )}

      <hr />

      {/* LIVE AGENT HANDOFF */}
      <h6 className="text-primary mb-1">Live Agent Handoff</h6>
      <p className="text-muted small mb-3">
        While a customer waits for or chats with a human agent, the bot stays silent.
        If the agent doesn&apos;t reply within the timeout below, the customer is sent an
        automatic reassurance message.
      </p>

      <Row className="mb-3">
        <Col md={6} className="mb-3">
          <Form.Label>Agent reply timeout (seconds)</Form.Label>
          <Form.Control
            type="number" min={5} max={600}
            placeholder="Default (30)"
            value={settings.agentReplyTimeout}
            onChange={(e) => set({ agentReplyTimeout: e.target.value })}
          />
          <Form.Text muted>
            How long a customer can wait without an agent reply before the auto message.
            Leave blank to use the default.
          </Form.Text>
        </Col>

        <Col md={6} className="mb-3">
          <Form.Label>Auto reply message</Form.Label>
          <Form.Control
            as="textarea" rows={3} maxLength={500}
            placeholder="Default: Thanks for your patience — our agent hasn't replied yet…"
            value={settings.agentWaitMessage}
            onChange={(e) => set({ agentWaitMessage: e.target.value })}
          />
          <Form.Text muted>Shown to the customer when the agent is slow to respond. Leave blank to use the default.</Form.Text>
        </Col>
      </Row>

      <Form.Check
        type="switch"
        id="agent-reassign-enabled"
        className="mb-2"
        label="Automatically move the chat to another agent when the assigned agent stays silent"
        checked={settings.agentReassignEnabled}
        onChange={(e) => set({ agentReassignEnabled: e.target.checked })}
      />
      <p className="text-muted small mb-3">
        The current ticket is closed and a new one is opened for the next free agent who
        hasn&apos;t already been tried on this chat. When every agent has been tried or none
        are free, the customer is told and the ticket is closed.
      </p>

      {settings.agentReassignEnabled && (
        <Row className="mb-3">
          <Col md={4} className="mb-3">
            <Form.Label>Reassign after (seconds)</Form.Label>
            <Form.Control
              type="number" min={10} max={3600}
              placeholder="Default (120)"
              value={settings.agentReassignTimeout}
              onChange={(e) => set({ agentReassignTimeout: e.target.value })}
            />
            <Form.Text muted>
              Should be longer than the reply timeout above. Leave blank to use the default.
            </Form.Text>
          </Col>

          <Col md={4} className="mb-3">
            <Form.Label>Reassigning message</Form.Label>
            <Form.Control
              as="textarea" rows={3} maxLength={500}
              placeholder="Default: Connecting you to another agent…"
              value={settings.agentReassignMessage}
              onChange={(e) => set({ agentReassignMessage: e.target.value })}
            />
            <Form.Text muted>Shown when the chat moves to a new agent.</Form.Text>
          </Col>

          <Col md={4} className="mb-3">
            <Form.Label>No agent available message</Form.Label>
            <Form.Control
              as="textarea" rows={3} maxLength={500}
              placeholder="Default: Sorry, no agent is available right now…"
              value={settings.agentUnavailableMessage}
              onChange={(e) => set({ agentUnavailableMessage: e.target.value })}
            />
            <Form.Text muted>Shown before the ticket is closed when no agent is free.</Form.Text>
          </Col>
        </Row>
      )}

      <hr />

      {/* ── Bot & Escalation Messages ── */}
      <h6 className="text-primary mb-1">Bot &amp; Escalation Messages</h6>
      <p className="text-muted small mb-3">
        Each field below shows the current default text — edit any of them to customise what the bot says.
        Clear a field (or leave it exactly as the default) to keep using the built-in default.
        The last four are internal labels shown in the ticket <em>Reason</em> column.
      </p>
      <Row>
        {[
          { key: "welcomeMessage",         label: "Welcome / greeting message",        ph: "Default: Hello! Welcome to Chatbot…", help: "First message shown when a chat opens." },
          { key: "fallbackMessage",        label: "Fallback (didn't understand)",      ph: "Default: Sorry, I didn't quite get that…", help: "Shown when the bot can't match the message. Option buttons still appear below it." },
          { key: "escConnectingMessage",   label: "Escalation — connecting",           ph: "Default: Connecting you to a human agent. Please wait a moment.", help: "When the customer clicks 'talk to a human'." },
          { key: "escEscalatedMessage",    label: "Escalation — escalated",            ph: "Default: Your request has been escalated to a human agent.", help: "When NLP routes the chat to a human." },
          { key: "escDomainMessage",       label: "Escalation — topic unclear",        ph: "Default: I'm connecting you to a human agent who can help with this.", help: "When the topic can't be identified." },
          { key: "escNomatchMessage",      label: "Escalation — after repeated misses", ph: "Default: I'm still not sure I understand — let me connect you…", help: "After several failed attempts to understand." },
          { key: "reasonLowConfidence",    label: "Reason — low confidence",           ph: "Default: Low confidence or handoff keyword detected", help: "Internal ticket reason label." },
          { key: "reasonQuickReply",       label: "Reason — quick-reply escalation",   ph: "Default: User selected escalation quick reply", help: "Internal label; the clicked value is appended." },
          { key: "reasonDomainNotMatched", label: "Reason — topic not matched",        ph: "Default: Auto-escalation: domain not matched", help: "Internal label; the message text is appended." },
          { key: "reasonNoMatch",          label: "Reason — no-match exhausted",       ph: "Default: No-match fallback exhausted", help: "Internal label; the attempt count is appended." },
        ].map(({ key, label, ph, help }) => (
          <Col md={6} className="mb-3" key={key}>
            <Form.Label>{label}</Form.Label>
            <Form.Control
              as="textarea" rows={2} maxLength={key.startsWith("reason") ? 255 : 500}
              placeholder={ph}
              value={settings[key]}
              onChange={(e) => set({ [key]: e.target.value })}
            />
            <Form.Text muted>{help}</Form.Text>
          </Col>
        ))}
      </Row>

      {/* ── Customer Satisfaction (CSAT) rating ── */}
      <hr className="my-4" />
      <h6 className="text-primary mb-1">Customer Satisfaction (CSAT)</h6>
      <p className="text-muted small mb-3">
        Decide whether the chat widget asks visitors to rate their conversation when it ends.
        Turn it off and no rating is ever shown; turn it on to collect scores that appear in
        <em> Analytics → CSAT</em>.
      </p>
      <Row className="align-items-end">
        <Col md={12} className="mb-3">
          <Form.Check
            type="switch"
            id="csat-enabled-switch"
            label="Ask visitors to rate the chat"
            checked={settings.csatEnabled}
            onChange={(e) => set({ csatEnabled: e.target.checked })}
          />
        </Col>
        <Col md={7} className="mb-3">
          <Form.Label>Rating question</Form.Label>
          <Form.Control
            maxLength={255}
            placeholder="How was your chat?"
            value={settings.csatQuestion}
            disabled={!settings.csatEnabled}
            onChange={(e) => set({ csatQuestion: e.target.value })}
          />
          <Form.Text muted>Shown above the 1–5 star rating in the widget.</Form.Text>
        </Col>
        <Col md={5} className="mb-3">
          <Form.Label>When to ask</Form.Label>
          <Form.Select
            value={settings.csatTrigger}
            disabled={!settings.csatEnabled}
            onChange={(e) => set({ csatTrigger: e.target.value })}
          >
            <option value="on_close">When the visitor closes the chat</option>
            <option value="on_resolve">When the ticket is resolved</option>
          </Form.Select>
          <Form.Text muted>Currently the widget asks on chat close.</Form.Text>
        </Col>
      </Row>

      {/* Add Keyword Modal */}
      <Modal show={showKeywordModal} onHide={() => setShowKeywordModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add Escalation Keyword</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Keyword</Form.Label>
            <Form.Control
              placeholder='e.g. "agent"'
              value={newKeyword}
              onChange={(e) => setNewKeyword(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addKeyword()}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Priority</Form.Label>
            <Form.Select value={newPriority} onChange={(e) => setNewPriority(e.target.value)}>
              <option value="HIGH">High</option>
              <option value="MEDIUM">Medium</option>
              <option value="LOW">Low</option>
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowKeywordModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={addKeyword}>Save</Button>
        </Modal.Footer>
      </Modal>

      {/* Save bar */}
      {hasChanges && (
        <div className="s-save-bar">
          <span className="s-save-bar-left">
            {lastSavedAt
              ? <>Last saved {lastSavedAt.toLocaleTimeString()}</>
              : "Unsaved changes"}
          </span>
          <div className="s-save-bar-right">
            <button className="s-btn s-btn-ghost" onClick={handleDiscard} disabled={saving}>
              Discard
            </button>
            <button
              className="s-btn s-btn-primary"
              onClick={handleSave}
              disabled={saving || !!contextTimeoutError || !!maxConvError}
            >
              {saving
                ? <><Spinner size="sm" animation="border" className="me-1" />Saving…</>
                : "Save changes"}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default SettingsConversation;
